/**
 * Lazy Loading Optimizer
 * Enhances native lazy loading with intersection observer
 * Reduces initial payload by deferring below-fold images
 */

// CRITICAL: Ensure first slide images are immediately visible
// This function forces visibility for slideshow/hero images with src (not data-src)
function forceSlideshowImagesVisible() {
  try {
    // Find all slideshow images with src (not data-src) - these should be visible immediately
    // Also handle images inside picture tags
    // CRITICAL: Also explicitly target .t4s-lcp-image class
    const immediateImages = document.querySelectorAll('.t4s-slideshow img[src]:not([data-src]), .t4s-hero-image img[src]:not([data-src]), .t4s-slideshow picture img[src]:not([data-src]), .t4s-hero-image picture img[src]:not([data-src]), .t4s-lcp-image[src]:not([data-src]), .t4s-hero-inner .t4s-lcp-image[src]:not([data-src])');
    immediateImages.forEach(function(img) {
      // Skip if src is a placeholder (data:image/gif)
      if (img.src && img.src.includes('data:image/gif')) {
        return;
      }
      
      // Force visibility with inline styles (highest priority)
      img.style.setProperty('opacity', '1', 'important');
      img.style.setProperty('visibility', 'visible', 'important');
      img.style.setProperty('display', 'block', 'important');
      img.classList.remove('lazyloading', 'lazyloadt4sing');
      img.classList.add('lazyloaded', 'lazyloadt4sed');
      
      // Hide corresponding lazyloadt4s-loader span (check both next sibling and parent's next sibling)
      let loader = img.nextElementSibling;
      if (!loader || !loader.classList.contains('lazyloadt4s-loader')) {
        // Check if parent (picture) has next sibling
        const parent = img.parentElement;
        if (parent && parent.tagName === 'PICTURE') {
          loader = parent.nextElementSibling;
        }
      }
      if (loader && loader.classList.contains('lazyloadt4s-loader')) {
        loader.style.setProperty('display', 'none', 'important');
        loader.style.setProperty('opacity', '0', 'important');
        loader.style.setProperty('visibility', 'hidden', 'important');
      }
    });
  } catch (e) {
    // Silently fail - non-critical
  }
}

// PERFORMANCE: Run immediately but use requestAnimationFrame for non-blocking execution
// BUG FIX: Single DOMContentLoaded listener to avoid duplicate execution
if (document.readyState === 'loading') {
  // If DOM is still loading, wait for it
  document.addEventListener('DOMContentLoaded', function() {
    requestAnimationFrame(forceSlideshowImagesVisible);
  }, { once: true });
} else {
  // DOM is ready, run in next frame to avoid blocking
  requestAnimationFrame(forceSlideshowImagesVisible);
}

// Run again after a short delay to catch any late-loading images
setTimeout(forceSlideshowImagesVisible, 100);
setTimeout(forceSlideshowImagesVisible, 500);

(function() {
  'use strict';

  // Check if IntersectionObserver is supported
  if (!('IntersectionObserver' in window)) {
    // Fallback to native lazy loading
    return;
  }

  // Configuration
  const config = {
    rootMargin: '50px', // Start loading 50px before image enters viewport
    threshold: 0.01
  };

  // Track loaded images
  const loadedImages = new Set();

  /**
   * Lazy load images using IntersectionObserver
   * CRITICAL: Do NOT interfere with theme's native lazysizes system
   */
  function initLazyLoading() {
    // Find all images that should be lazy loaded
    // EXCLUDE images with lazyloadt4s class or lazysizes classes - those are handled by theme's lazysizes library
    const images = document.querySelectorAll('img[loading="lazy"]:not([data-lazy-loaded]):not(.lazyloadt4s):not([class*="lazyloadt4s"]):not(.lazyloading):not(.lazyloaded)');
    
    if (images.length === 0) return;

    const imageObserver = new IntersectionObserver(function(entries, observer) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          const img = entry.target;
          
          // Skip if already loaded
          if (loadedImages.has(img.src)) {
            observer.unobserve(img);
            return;
          }

          // Skip if theme's lazysizes is handling this image
          // Check for both lazysizes classes (lazyloading/lazyloaded) and theme classes (lazyloadt4s/lazyloadt4sing/lazyloadt4sed)
          if (img.classList.contains('lazyloadt4s') || 
              img.classList.contains('lazyloadt4sing') || 
              img.classList.contains('lazyloadt4sed') ||
              img.classList.contains('lazyloading') ||
              img.classList.contains('lazyloaded')) {
            observer.unobserve(img);
            return;
          }

          // Load the image
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          
          // Load srcset if available
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
            img.removeAttribute('data-srcset');
          }

          // Mark as loaded
          img.setAttribute('data-lazy-loaded', 'true');
          loadedImages.add(img.src);
          
          // Remove observer after loading
          observer.unobserve(img);
        }
      });
    }, config);

    // Observe all lazy images
    images.forEach(function(img) {
      imageObserver.observe(img);
    });
  }

  /**
   * Optimize background images
   * CRITICAL: Do NOT interfere with theme's native lazysizes system
   * Only handle elements that are NOT managed by lazysizes
   */
  function optimizeBackgroundImages() {
    // FIRST: Handle hero sections immediately (synchronously, before any async operations)
    // Only process hero blocks that are NOT managed by lazysizes
    // Exclude both lazysizes classes (lazyloading/lazyloaded) and theme classes (lazyloadt4s)
    const heroBgElements = document.querySelectorAll('.t4s-hero-block[data-bg-src]:not(.lazyloadt4s):not([class*="lazyloadt4s"]):not(.lazyloading):not(.lazyloaded), .kt-hero-enhanced__background[data-bg-src]:not(.lazyloadt4s):not([class*="lazyloadt4s"]):not(.lazyloading):not(.lazyloaded)');
    heroBgElements.forEach(function(element) {
      const bgSrc = element.getAttribute('data-bg-src');
      if (!bgSrc) return;
      
      // Ensure background is set and visible
      element.style.backgroundImage = `url('${bgSrc}')`;
      element.style.opacity = '1';
      element.style.visibility = 'visible';
      element.style.backgroundSize = 'cover';
      element.style.backgroundPosition = 'center';
      element.style.backgroundRepeat = 'no-repeat';
      
      // Remove lazy loading classes that might hide it
      element.classList.remove('lazyloadt4sing', 'lazyloadt4s');
      element.classList.add('lazyloadt4sed');
      
      // Keep data-bg-src for now (don't remove it yet - theme might need it)
      // But ensure the inline style takes precedence
    });
    
    // THEN: Handle other background elements with IntersectionObserver
    // EXCLUDE elements managed by lazysizes (lazyloadt4s-loader spans are handled by theme)
    // Also exclude lazysizes classes (lazyloading/lazyloaded)
    const bgElements = document.querySelectorAll('[data-bg-src]:not(.t4s-hero-block):not(.kt-hero-enhanced__background):not(.lazyloadt4s-loader):not(.lazyloadt4s):not([class*="lazyloadt4s"]):not(.lazyloading):not(.lazyloaded)');
    
    bgElements.forEach(function(element) {
      const bgSrc = element.getAttribute('data-bg-src');
      if (!bgSrc) return;

      // For below-fold elements, use IntersectionObserver
      const bgObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            const el = entry.target;
            const src = el.getAttribute('data-bg-src');
            if (src) {
              el.style.backgroundImage = `url('${src}')`;
              el.removeAttribute('data-bg-src');
              el.classList.remove('lazyloadt4sing', 'lazyloadt4s');
              el.classList.add('lazyloadt4sed');
              
              // Also ensure opacity is set to 1 for fadeIn animations
              if (el.classList.contains('t4s-lz--fadeIn')) {
                el.style.opacity = '1';
              }
              
              bgObserver.unobserve(el);
            }
          }
        });
      }, config);

      bgObserver.observe(element);
    });
  }

  /**
   * Preload critical images
   */
  function preloadCriticalImages() {
    // Find first few product images in viewport
    const productImages = document.querySelectorAll('.t4s-product-img img, .product-item img');
    const viewportHeight = window.innerHeight;
    
    productImages.forEach(function(img, index) {
      if (index < 3) { // Preload first 3 product images
        const rect = img.getBoundingClientRect();
        if (rect.top < viewportHeight * 1.5) {
          // Image is near viewport, preload
          if (img.dataset.src) {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = img.dataset.src;
            document.head.appendChild(link);
          }
        }
      }
    });
  }

  // CRITICAL: Run hero background optimization IMMEDIATELY (synchronously)
  // This ensures hero images are visible on first paint, before any async operations
  // BUT: Do NOT interfere with elements managed by lazysizes
  (function() {
    try {
      // Only process hero blocks that are NOT managed by lazysizes
      // Exclude both lazysizes classes (lazyloading/lazyloaded) and theme classes (lazyloadt4s)
      const heroBgElements = document.querySelectorAll('.t4s-hero-block:not(.lazyloadt4s):not([class*="lazyloadt4s"]):not(.lazyloading):not(.lazyloaded), .kt-hero-enhanced__background:not(.lazyloadt4s):not([class*="lazyloadt4s"]):not(.lazyloading):not(.lazyloaded)');
      heroBgElements.forEach(function(element) {
        // If it has data-bg-src, use it; otherwise keep existing inline style
        const bgSrc = element.getAttribute('data-bg-src');
        if (bgSrc) {
          element.style.backgroundImage = `url('${bgSrc}')`;
        }
        
        // Ensure visibility
        element.style.opacity = '1';
        element.style.visibility = 'visible';
        element.style.backgroundSize = element.style.backgroundSize || 'cover';
        element.style.backgroundPosition = element.style.backgroundPosition || 'center';
        element.style.backgroundRepeat = element.style.backgroundRepeat || 'no-repeat';
        
        // Remove lazy loading classes that might hide it
        element.classList.remove('lazyloadt4sing', 'lazyloadt4s');
        element.classList.add('lazyloadt4sed');
      });
    } catch (e) {
      // Silently fail - non-critical
    }
  })();

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initLazyLoading();
      optimizeBackgroundImages();
      preloadCriticalImages();
    });
  } else {
    initLazyLoading();
    optimizeBackgroundImages();
    preloadCriticalImages();
  }

  // CRITICAL: Force search result images to stay visible when lazy loading classes are added
  function forceSearchImagesVisible() {
    try {
      const searchImages = document.querySelectorAll('.t4s-main-collection-search .t4s-product .t4s-product-img img, .t4s-main-collection-search .t4s-product .t4s-product-img .t4s-product-image, .t4s-main-collection-search .t4s-product .t4s-product-img picture img');
      searchImages.forEach(function(img) {
        // Skip placeholder images (data:image/gif or data:image/svg)
        const src = img.src || img.getAttribute('data-src') || '';
        if (src && (src.includes('data:image/gif') || src.includes('data:image/svg') || src === '')) {
          return;
        }
        // Force visibility with inline styles (highest priority) - override ANY opacity/visibility
        img.style.setProperty('opacity', '1', 'important');
        img.style.setProperty('visibility', 'visible', 'important');
        img.style.setProperty('display', 'block', 'important');
        // Also remove lazy loading classes that might hide images
        if (img.classList.contains('lazyloadt4sing')) {
          img.classList.remove('lazyloadt4sing');
          img.classList.add('lazyloadt4sed');
        }
      });
    } catch (e) {
      console.error('Error in forceSearchImagesVisible:', e);
    }
  }

  // Re-run for dynamically added content
  if ('MutationObserver' in window) {
    const observer = new MutationObserver(function(mutations) {
      let shouldReinit = false;
      let searchImagesChanged = false;
      
      mutations.forEach(function(mutation) {
        // Watch for class changes on search result images
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.tagName === 'IMG' && target.closest('.t4s-main-collection-search .t4s-product .t4s-product-img')) {
            // If lazyloadt4sing class was added, force visibility
            if (target.classList.contains('lazyloadt4sing') || target.classList.contains('lazyloadt4sed')) {
              target.style.setProperty('opacity', '1', 'important');
              target.style.setProperty('visibility', 'visible', 'important');
              target.style.setProperty('display', 'block', 'important');
              searchImagesChanged = true;
            }
          }
        }
        
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) { // Element node
              // Handle slideshow images with src (not data-src) - these should be immediately visible
              if (node.tagName === 'IMG' && node.hasAttribute('src') && !node.hasAttribute('data-src')) {
                if (node.closest('.t4s-slideshow, .t4s-hero-image')) {
                  // Skip if src is a placeholder
                  if (!node.src || !node.src.includes('data:image/gif')) {
                    node.style.setProperty('opacity', '1', 'important');
                    node.style.setProperty('visibility', 'visible', 'important');
                    node.style.setProperty('display', 'block', 'important');
                    node.classList.remove('lazyloading', 'lazyloadt4sing');
                    node.classList.add('lazyloaded', 'lazyloadt4sed');
                    
                    // Hide corresponding lazyloadt4s-loader span (check both next sibling and parent's next sibling)
                    let loader = node.nextElementSibling;
                    if (!loader || !loader.classList.contains('lazyloadt4s-loader')) {
                      const parent = node.parentElement;
                      if (parent && parent.tagName === 'PICTURE') {
                        loader = parent.nextElementSibling;
                      }
                    }
                    if (loader && loader.classList.contains('lazyloadt4s-loader')) {
                      loader.style.setProperty('display', 'none', 'important');
                      loader.style.setProperty('opacity', '0', 'important');
                      loader.style.setProperty('visibility', 'hidden', 'important');
                    }
                  }
                }
                // Also handle search result images
                if (node.closest('.t4s-main-collection-search .t4s-product .t4s-product-img')) {
                  if (!node.src || !node.src.includes('data:image/gif')) {
                    node.style.setProperty('opacity', '1', 'important');
                    node.style.setProperty('visibility', 'visible', 'important');
                    node.style.setProperty('display', 'block', 'important');
                    searchImagesChanged = true;
                  }
                }
              }
              
              // Also check if a picture element was added
              if (node.tagName === 'PICTURE' && node.closest('.t4s-slideshow, .t4s-hero-image')) {
                const img = node.querySelector('img[src]:not([data-src])');
                if (img && img.src && !img.src.includes('data:image/gif')) {
                  forceSlideshowImagesVisible();
                }
              }
              
              // Only reinit if the added node is an image and NOT managed by lazysizes
              if ((node.tagName === 'IMG' && !node.classList.contains('lazyloadt4s') && !node.classList.contains('lazyloadt4sing') && !node.classList.contains('lazyloadt4sed')) || 
                  (node.querySelector('img:not(.lazyloadt4s):not(.lazyloadt4sing):not(.lazyloadt4sed)'))) {
                shouldReinit = true;
              }
            }
          });
        }
      });
      
      if (shouldReinit) {
        initLazyLoading();
        optimizeBackgroundImages();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    
    // Also periodically force search images visible (in case MutationObserver misses something)
    setInterval(function() {
      forceSearchImagesVisible();
    }, 1000);
  }
  
  // Run immediately on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      forceSearchImagesVisible();
      // Run again after a short delay to catch images that load after DOMContentLoaded
      setTimeout(forceSearchImagesVisible, 500);
      setTimeout(forceSearchImagesVisible, 2000);
    });
  } else {
    forceSearchImagesVisible();
    setTimeout(forceSearchImagesVisible, 500);
    setTimeout(forceSearchImagesVisible, 2000);
  }
})();
